import * as React from "react";
import {
  Alert,
  Box,
  Button,
  Divider,
  Pagination,
  SelectChangeEvent,
  Stack,
} from "@mui/material";
import { useLanguage } from "../../Language";
import { Dayjs } from "dayjs";
import PrintIcon from "@mui/icons-material/Print";
import { useOutletContext, useSearchParams } from "react-router-dom";
import { HostSystemItemSortType } from "@tlc/ls2pac/dto/HostSystemItemSortType";
import { HostSystemItemView } from "@tlc/ls2pac/dto/HostSystemItemView";
import AccountPrintLoanHistoryDialog from "./AccountPrintLoansHistoryDialog";
import { DateRangeDialog } from "../../DateRangeDialog";
import { getDateString } from "./AccountPrintLoansDialog";
import { LoanHistorySortControl } from "./HostSystemItemSortControl";
import { LoanHistoryItem } from "./LoanHistoryItem";
import { SharedViewHeader } from "./SharedViewHeader";
import { useLSResultsSelection } from "../search/LSResultsSelectionContext";
import { useDialog } from "@tlc/ui/dialog/useDialog";

export interface SharedLoanHistoryProps {
  page: number;
  pageSize: number;
  sort: HostSystemItemSortType;
  totalLoans: number;
  displayedLoanHistory: HostSystemItemView[];
  emptyMessageKey: string;
  isStudent: boolean;

  requiresDateRange: boolean;
  isRateAndReviewEnabled?: boolean;
  fetchPrintData: (
    startDate?: Date,
    endDate?: Date,
  ) => Promise<HostSystemItemView[]>;
}

export const SharedLoanHistoryView: React.FC<SharedLoanHistoryProps> = ({
  page,
  pageSize,
  sort,
  totalLoans,
  displayedLoanHistory,
  emptyMessageKey,
  requiresDateRange,
  isRateAndReviewEnabled,
  fetchPrintData,
  isStudent,
}) => {
  const { translate } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    selectedLength,
    isAllIndeterminate,
    isAllSelected,
    handleSelectAllChange,
  } = useLSResultsSelection();

  // Neat, this just pulls the context value passed from the outlet in AccountHistoryPage no import needed
  const { scrollToTop } = useOutletContext<{ scrollToTop: () => void }>();

  const [isPrintLoading, setIsPrintLoading] = React.useState(false);
  const [loanHistoryForPrint, setLoanHistoryForPrint] = React.useState<
    HostSystemItemView[]
  >([]);
  const {
    open: printDialogOpen,
    closeDialog: closePrintDialog,
    openDialog: openPrintDialog,
  } = useDialog();
  const {
    open: dateRangeDialogOpen,
    closeDialog: closeDateRangeDialog,
    openDialog: openDateRangeDialog,
  } = useDialog();
  const [dateRangeForPrint, setDateRangeForPrint] = React.useState({
    startDate: "",
    endDate: "",
  });

  const pages = totalLoans ? Math.ceil(totalLoans / pageSize) : 0;

  const handlePageChange = (
    _event: React.ChangeEvent<unknown>,
    newPage: number,
  ) => {
    searchParams.set("page", newPage.toString());
    setSearchParams(searchParams);
    scrollToTop();
  };

  const handleSortChange = (event: SelectChangeEvent) => {
    const newSort = event.target.value as HostSystemItemSortType;
    searchParams.set("page", "1");
    searchParams.set("sort", newSort);
    setSearchParams(searchParams);
  };

  const handlePrintClick = async () => {
    if (requiresDateRange) {
      openDateRangeDialog();
    } else {
      // Child flow: no date range, fetch all immediately
      setIsPrintLoading(true);
      try {
        const data = await fetchPrintData();
        data.forEach((loan) => {
          if (loan.outDate) loan.outDateString = getDateString(loan.outDate);
        });
        setLoanHistoryForPrint(data);
        setDateRangeForPrint({ startDate: "", endDate: "" });
        openPrintDialog();
      } catch (e) {
        console.error("Error fetching print loan history", e);
      } finally {
        setIsPrintLoading(false);
      }
    }
  };

  const handleDateRangeConfirm = async (startDate: Dayjs, endDate: Dayjs) => {
    closeDateRangeDialog();
    setIsPrintLoading(true);
    setDateRangeForPrint({
      startDate: startDate.format("L"),
      endDate: endDate.format("L"),
    });
    try {
      const data = await fetchPrintData(startDate.toDate(), endDate.toDate());
      data.forEach((loan) => {
        if (loan.outDate) loan.outDateString = getDateString(loan.outDate);
      });
      setLoanHistoryForPrint(data);
      openPrintDialog();
    } catch (e) {
      console.error("Error fetching print loan history by date", e);
    } finally {
      setIsPrintLoading(false);
    }
  };

  return (
    <>
      <SharedViewHeader
        labelKey={translate("loans")}
        count={totalLoans}
        showSelectAll={false}
        selectedLength={selectedLength}
        isAllIndeterminate={isAllIndeterminate}
        isAllSelected={isAllSelected}
        handleSelectAllChange={handleSelectAllChange}
        headerActions={
          <Box
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            {!isStudent && (
              <Button
                onClick={handlePrintClick}
                startIcon={<PrintIcon aria-hidden="true" />}
                disabled={isPrintLoading}
              >
                {`${translate("print")} ${translate("loanHistory")}`}
              </Button>
            )}
          </Box>
        }
        sortControl={
          <LoanHistorySortControl value={sort} onChange={handleSortChange} />
        }
      />

      <>
        {totalLoans === 0 ? (
          <Alert sx={{ mt: 2 }} severity="info">
            {translate(emptyMessageKey)}
          </Alert>
        ) : (
          <>
            <Box>
              {displayedLoanHistory.map((loan) => (
                <React.Fragment key={loan.id}>
                  <Divider />
                  <LoanHistoryItem
                    loan={loan}
                    canRateAndReview={isRateAndReviewEnabled}
                  />
                </React.Fragment>
              ))}
            </Box>
            {(totalLoans > pageSize || page > pages) && (
              <Box display="flex" justifyContent="center" mt={2}>
                <Stack spacing={2}>
                  <Pagination
                    count={pages}
                    color="primary"
                    variant="text"
                    page={page}
                    onChange={handlePageChange}
                    sx={{
                      "& .MuiPagination-ul, .MuiPagination-ul li": {
                        border: "none",
                      },
                    }}
                  />
                </Stack>
              </Box>
            )}
          </>
        )}
      </>

      {requiresDateRange && (
        <DateRangeDialog
          open={dateRangeDialogOpen}
          onClose={() => closeDateRangeDialog()}
          onConfirm={handleDateRangeConfirm}
        />
      )}

      <AccountPrintLoanHistoryDialog
        loanHistory={loanHistoryForPrint}
        startDate={dateRangeForPrint.startDate}
        endDate={dateRangeForPrint.endDate}
        open={printDialogOpen}
        onClose={() => {
          closePrintDialog();
          setLoanHistoryForPrint([]);
        }}
      />
    </>
  );
};
